import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import authService from "../../services/authService";
import { Oval } from 'react-loader-spinner';

export default function Profile() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        bank_name: "",
        bank_account_number: "",
        bank_account_name: "",
        birth_date: "",
        address: "",
        custom_bank: "",
        user_type: "",
        company_name: "",
        company_address: "",
        company_phone: "",
        npwp: "",
        company_email: ""
    });
    
    const bankOptions = [
        "BCA", "Mandiri", "BNI", "BRI", "BTN", "CIMB Niaga",
        "Permata Bank", "Danamon", "Maybank", "OCBC NISP",
        "Bank Mega", "Sinarmas", "Lainnya"
    ];

    useEffect(() => {
        // Check if user is logged in
        const checkAuth = async () => {
            try {
                if (!authService.isLoggedIn()) {
                    navigate('/auth-login');
                    return;
                }

                try {
                    // Get current user profile data and check completion status
                    const profileData = await authService.verifyProfileCompletion();
                    const currentUser = profileData.user;
                    
                    setUser(currentUser);
                    
                    // Check if the bank_name is in the standard list or should be set as Other
                    const isStandardBank = bankOptions.includes(currentUser.bank_name);
                    
                    // Pre-fill form with current user data
                    setFormData(prevData => ({
                        ...prevData,
                        name: currentUser.name || "",
                        email: currentUser.email || "",
                        phone: currentUser.phone || "",
                        bank_name: isStandardBank ? currentUser.bank_name : "Lainnya",
                        bank_account_number: currentUser.bank_account_number || "",
                        bank_account_name: currentUser.bank_account_name || "",
                        birth_date: currentUser.birth_date || "",
                        address: currentUser.address || "",
                        custom_bank: isStandardBank ? "" : currentUser.bank_name,
                        user_type: currentUser.user_type || "personal",
                        company_name: currentUser.company_name || "",
                        company_address: currentUser.company_address || "",
                        company_phone: currentUser.company_phone || "",
                        npwp: currentUser.npwp || "",
                        company_email: currentUser.company_email || ""
                    }));
                } catch (err) {
                    console.error("Error fetching user profile:", err);
                    // If we can't get the full profile, at least get the basic user info
                    const currentUser = authService.getCurrentUser();
                    setUser(currentUser);
                    
                    setFormData(prevData => ({
                        ...prevData,
                        name: currentUser.name || "",
                        email: currentUser.email || "",
                    }));
                }

                setLoading(false);
            } catch (err) {
                console.error("Authentication error:", err);
                navigate('/auth-login');
            }
        };

        checkAuth();
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleBankChange = (e) => {
        const value = e.target.value;
        setFormData(prevData => ({
            ...prevData,
            bank_name: value,
            custom_bank: value === "Lainnya" ? prevData.custom_bank : ""
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(false);

        // Prepare data for submission
        const dataToSubmit = { ...formData };
        
        // If "Other" bank is selected, use the custom bank name
        if (formData.bank_name === "Lainnya" && formData.custom_bank) {
            dataToSubmit.bank_name = formData.custom_bank;
        }
        
        // Remove the custom_bank field as it's not needed for the API
        delete dataToSubmit.custom_bank;

        try {
            const response = await authService.updateProfile(dataToSubmit);
            
            // Update user data based on API response format
            const updatedUser = response.user || response;
            
            if (updatedUser) {
                setUser(updatedUser);
                
                // Update localStorage with the latest user data
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }
            
            setSuccess(true);
            
            // Reset form with updated data
            setFormData(prevData => ({
                ...prevData,
                ...updatedUser,
                custom_bank: ""
            }));
            
            // Redirect to dashboard after successful update
            setTimeout(() => {
                navigate('/user/dashboard');
            }, 1500);
        } catch (err) {
            setError(err.message || "Failed to update profile. Please try again.");
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const isProfileComplete = () => {
        let requiredFields = ['name', 'email', 'phone', 'bank_name', 'bank_account_number', 'bank_account_name', 'birth_date', 'address'];
        
        // Add corporate fields if user is perusahaan
        if (formData.user_type === 'perusahaan') {
            requiredFields = [...requiredFields, 'company_name', 'company_address', 'company_phone', 'npwp', 'company_email'];
        }
        
        return requiredFields.every(field => formData[field] && formData[field].trim() !== '');
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex justify-content-center align-items-center">
                <Oval
                    visible={true}
                    height="80"
                    width="80"
                    color="#4fa94d"
                    ariaLabel="oval-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                />
                <p className="ms-3">Memuat profil...</p>
            </div>
        );
    }

    return (
        <>
            <Navbar navClass="defaultscroll sticky" menuClass="navigation-menu nav-left" />
            
            <section className="section mt-5 pt-4">
                <div className="container mt-100 mt-60">
                    <div className="row">
                        <div className="col-lg-4 col-md-6 col-12 d-lg-block">
                            <div className="sidebar sticky-bar p-4 rounded bg-light">
                                <div className="text-center">
                                    <h5 className="mb-0">{user?.name || "Pengguna"}</h5>
                                    <p className="text-muted mt-1 mb-0">{user?.email}</p>
                                </div>

                                <div className="mt-4">
                                    <ul className="list-unstyled sidebar-nav mb-0">
                                        <li className="navbar-item"><Link to="/user/dashboard" className="navbar-link"><i className="mdi mdi-view-dashboard me-2"></i>Dasbor</Link></li>
                                        <li className="navbar-item"><Link to="/user/commissions" className="navbar-link"><i className="mdi mdi-currency-usd me-2"></i>Komisi</Link></li>
                                        <li className="navbar-item active"><Link to="/user/profile" className="navbar-link"><i className="mdi mdi-account me-2"></i>Profil</Link></li>
                                        <li className="navbar-item">
                                            <button 
                                                type="button" 
                                                className="navbar-link btn btn-link text-start w-100 border-0 p-0" 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    try {
                                                        authService.logout();
                                                        navigate('/auth-login', { replace: true });
                                                    } catch (error) {
                                                        console.error('Logout error:', error);
                                                        // Still navigate even if logout fails
                                                        navigate('/auth-login', { replace: true });
                                                    }
                                                }}
                                            >
                                                <i className="mdi mdi-logout me-2"></i>Keluar
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-8 col-12">
                            <div className="card border-0 rounded shadow">
                                <div className="card-body">
                                    <h5 className="mb-0">Pengaturan Profil</h5>
                                    
                                    {error && (
                                        <div className="alert alert-danger mt-3" role="alert">
                                            {error}
                                        </div>
                                    )}
                                    
                                    {success && (
                                        <div className="alert alert-success mt-3" role="alert">
                                            Profil berhasil diperbarui!
                                        </div>
                                    )}
                                    
                                    {!isProfileComplete() && (
                                        <div className="alert alert-warning mt-3" role="alert">
                                            <strong>Harap lengkapi profil Anda!</strong><br />
                                            Semua kolom wajib diisi untuk dapat membuat kode referral.
                                        </div>
                                    )}
                                    
                                    <form onSubmit={handleSubmit} className="mt-4">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Nama Lengkap</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Email</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        required
                                                        readOnly
                                                    />
                                                    <small className="text-muted">Email tidak dapat diubah</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Nomor Telepon</label>
                                                    <input
                                                        type="tel"
                                                        className="form-control"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Tanggal Lahir</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="birth_date"
                                                        value={formData.birth_date}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <label className="form-label">Alamat</label>
                                                    <textarea
                                                        className="form-control"
                                                        name="address"
                                                        value={formData.address}
                                                        onChange={handleChange}
                                                        rows="3"
                                                        required
                                                    ></textarea>
                                                </div>
                                            </div>
                                            
                                            {/* Corporate Fields */}
                                            {formData.user_type === 'perusahaan' && (
                                                <>
                                                    <div className="col-12">
                                                        <h6 className="mt-4 mb-3">Informasi Perusahaan</h6>
                                                    </div>
                                                    
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Nama Perusahaan</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                name="company_name"
                                                                value={formData.company_name}
                                                                onChange={handleChange}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Telepon Perusahaan</label>
                                                            <input
                                                                type="tel"
                                                                className="form-control"
                                                                name="company_phone"
                                                                value={formData.company_phone}
                                                                onChange={handleChange}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="col-12">
                                                        <div className="mb-3">
                                                            <label className="form-label">Alamat Perusahaan</label>
                                                            <textarea
                                                                className="form-control"
                                                                name="company_address"
                                                                value={formData.company_address}
                                                                onChange={handleChange}
                                                                rows="3"
                                                                required
                                                            ></textarea>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">NPWP</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                name="npwp"
                                                                value={formData.npwp}
                                                                onChange={handleChange}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Email Perusahaan</label>
                                                            <input
                                                                type="email"
                                                                className="form-control"
                                                                name="company_email"
                                                                value={formData.company_email}
                                                                onChange={handleChange}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                            
                                            <div className="col-12">
                                                <h6 className="mt-2 mb-3">Informasi Bank</h6>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Nama Bank</label>
                                                    <select
                                                        className="form-select"
                                                        name="bank_name"
                                                        value={formData.bank_name}
                                                        onChange={handleBankChange}
                                                        required
                                                    >
                                                        <option value="">Pilih Bank</option>
                                                        {bankOptions.map((bank, index) => (
                                                            <option key={index} value={bank}>
                                                                {bank === "Lainnya" ? "Lainnya" : bank}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            
                                            {formData.bank_name === "Lainnya" && (
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">Nama Bank Lainnya</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="custom_bank"
                                                            value={formData.custom_bank}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Nomor Rekening</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="bank_account_number"
                                                        value={formData.bank_account_number}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Nama Pemilik Rekening</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="bank_account_name"
                                                        value={formData.bank_account_name}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <button 
                                            type="submit" 
                                            className="btn btn-primary mt-3"
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Menyimpan...
                                                </>
                                            ) : "Simpan Perubahan"}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
} 